import { clusterHotels } from './clusterHotels';
import { fromPixelPoint, isValidCoords, toPixelPoint, WORLD_TILE_SIZE_PX } from './mercator';
import {
  DEGENERATE_BOUNDS_EPS,
  FIRST_FIT_MAX_ZOOM,
  MAP_CARD_GAP_PX,
  MAP_MARGIN,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  SINGLE_HOTEL_ZOOM,
} from '../config/map';
import type {
  MapBounds,
  MapCamera,
  MapCluster,
  MapClusterAction,
  MapHotel,
  MapMargin,
  MapViewportSize,
  MapZoomLimits,
} from '../model/types';

function clampZoom(zoom: number, limits: MapZoomLimits): number {
  return Math.min(Math.max(zoom, limits.minZoom), limits.maxZoom);
}

export function getHotelsBounds(hotels: readonly MapHotel[]): MapBounds | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  hotels.forEach((hotel) => {
    if (!isValidCoords(hotel.coords)) return;

    west = Math.min(west, hotel.coords.longitude);
    east = Math.max(east, hotel.coords.longitude);
    south = Math.min(south, hotel.coords.latitude);
    north = Math.max(north, hotel.coords.latitude);
  });

  if (!Number.isFinite(west)) return null;

  return { west, south, east, north };
}

export function isDegenerateBounds(bounds: MapBounds): boolean {
  return bounds.east - bounds.west < DEGENERATE_BOUNDS_EPS && bounds.north - bounds.south < DEGENERATE_BOUNDS_EPS;
}

export function fitBounds(
  bounds: MapBounds,
  viewport: MapViewportSize,
  margin: MapMargin,
  limits: MapZoomLimits,
): MapCamera {
  const [marginTop, marginRight, marginBottom, marginLeft] = margin;
  const effectiveWidth = Math.max(1, viewport.width - marginLeft - marginRight);
  const effectiveHeight = Math.max(1, viewport.height - marginTop - marginBottom);

  const northWest = toPixelPoint({ latitude: bounds.north, longitude: bounds.west }, 0);
  const southEast = toPixelPoint({ latitude: bounds.south, longitude: bounds.east }, 0);

  const rawSpanX = southEast.x - northWest.x;
  const spanX = rawSpanX < 0 ? rawSpanX + WORLD_TILE_SIZE_PX : rawSpanX;
  const spanY = southEast.y - northWest.y;

  const zoomX = spanX > 0 ? Math.log2(effectiveWidth / spanX) : Infinity;
  const zoomY = spanY > 0 ? Math.log2(effectiveHeight / spanY) : Infinity;
  const fittedZoom = Math.min(zoomX, zoomY);
  const zoom = clampZoom(Number.isFinite(fittedZoom) ? Math.floor(fittedZoom) : SINGLE_HOTEL_ZOOM, limits);

  const center = fromPixelPoint({ x: (northWest.x + southEast.x) / 2, y: (northWest.y + southEast.y) / 2 }, 0);

  return { center, zoom };
}

export function getFirstFitCamera(
  hotels: readonly MapHotel[],
  viewport: MapViewportSize,
  margin: MapMargin,
): MapCamera | null {
  const bounds = getHotelsBounds(hotels);

  if (!bounds) return null;

  return fitBounds(bounds, viewport, margin, { minZoom: MAP_MIN_ZOOM, maxZoom: FIRST_FIT_MAX_ZOOM });
}

function isClusterSplitBy(cluster: MapCluster, zoom: number): boolean {
  return clusterHotels(cluster.hotels, { zoom }).length > 1;
}

export function getClusterCameraAction(
  cluster: MapCluster,
  viewport: MapViewportSize,
  margin: MapMargin,
  currentZoom: number,
): MapClusterAction {
  if (currentZoom >= MAP_MAX_ZOOM || !isClusterSplitBy(cluster, MAP_MAX_ZOOM)) {
    return { kind: 'list', hotels: cluster.hotels };
  }

  const camera = isDegenerateBounds(cluster.bounds)
    ? { center: cluster.anchor, zoom: MAP_MAX_ZOOM }
    : fitBounds(cluster.bounds, viewport, margin, {
        minZoom: Math.min(Math.floor(currentZoom) + 1, MAP_MAX_ZOOM),
        maxZoom: MAP_MAX_ZOOM,
      });

  return { kind: 'fly', camera };
}

export function getCardMargin(mapBottom: number, cardTop: number): MapMargin {
  const [top, right, bottom, left] = MAP_MARGIN;

  return [top, right, Math.max(bottom, mapBottom - cardTop + MAP_CARD_GAP_PX), left];
}
